import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Logo } from '~shared/components/logo'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import { SearchIcon } from '~shared/icons/search'
import { Drawer, DrawerTrigger } from '@/modules/components/ui/drawer'
import { HeaderDrawer } from './header-drawer'
import { getPopularPrograms } from '~shared/api/programs-popular/getPopularPrograms'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { usePathname } from 'next/navigation'
import { Button } from '~shared/components/button-v2'
import { LogoHeaderMobile } from '~shared/components/logo-header-mobile'
import { BurgerMenuDrawer } from './burger-menu-drawer'
import { useBreakpoint } from '~shared/hooks/useBreakpoint'
import { cn } from '~shared/utils/cn'

type HeaderProps = {
  categories: Awaited<ReturnType<typeof getCategoriesData>>
  popularPrograms: Awaited<ReturnType<typeof getPopularPrograms>>
}

export const HeaderUpdated: React.FC<HeaderProps> = ({
  categories,
  popularPrograms
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const breakpoint = useBreakpoint()
  const router = useRouter()

  // Поиск — один общий Drawer на весь хедер (см. ниже), три триггера
  // внутри него делят один и тот же open-стейт/контент.
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  // Бургер-меню — свой отдельный Drawer, тоже controlled.
  const [isBurgerOpen, setIsBurgerOpen] = React.useState(false)

  const navLinks = [
    { name: 'Каталог', slug: '/programmy' },
    { name: 'Бакалавриат', slug: '/bakalavriat' },
    { name: 'Магистратура', slug: '/magistratura' },
    { name: 'Курсы ДПО', slug: '/kursy' },
    { name: 'Колледж', slug: '/college' },
    { name: 'MBA', slug: '/mba' },
    { name: 'Журнал', slug: '/journal' },
    { name: 'О нас', slug: '/contact' },
    { name: 'Мероприятия', slug: '/meropriyatiya' },
    // { name: 'День открытых дверей', slug: '/den-otkrytyh-dverey' },
    { name: 'Слушателям', slug: '/slushateli' }
  ]
  const pathname = usePathname()

  const isActive = (slug: string) => {
    const cleanSlug = slug.replace(/^\//, '')
    const currentPath = pathname

    // Быстрые проверки
    if (cleanSlug === '' && currentPath === '/') return true
    if (currentPath === slug) return true

    // Проверяем вхождение slug в путь
    const slugPattern = new RegExp(`(^|/|--)${cleanSlug}(/|$|--)`)
    return slugPattern.test(currentPath ?? '')
  }

  // Закрываем поиск и бургер при любой успешной смене маршрута —
  // не важно, remount-ится хедер между страницами или нет.
  React.useEffect(() => {
    const closeAllDrawers = () => {
      setIsSearchOpen(false)
      setIsBurgerOpen(false)
    }
    router.events.on('routeChangeComplete', closeAllDrawers)
    return () => {
      router.events.off('routeChangeComplete', closeAllDrawers)
    }
  }, [router])

  return (
    <header className='pt-[60px] pb-[30px] md:pt-[24px] md:pb-[40px]'>
      {/*
        Один Drawer для поиска на весь хедер: три DrawerTrigger ниже
        (мобильный верхний, десктопный xl-nav, мобильный/md nav) делят
        общий контекст, а HeaderDrawer (контент) рендерится один раз
        в конце — чтобы не открывалось сразу несколько drawer'ов.
      */}
      <Drawer
        direction='top'
        handleOnly={breakpoint === 'xs'}
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}>
        <div className='container container-slim'>
          <div className='flex md:gap-[24px] lg:gap-[40px] items-center md:items-start w-full lg:w-auto'>
            <div className='flex items-center gap-[16px]'>
              <Link href='/'>
                <Logo
                  id='Header'
                  className='text-tertiary hidden md:inline-block'
                />
                <LogoHeaderMobile className='md:hidden' />
              </Link>
              <DrawerTrigger asChild>
                <Button
                  size='sm'
                  className={cn(
                    'md:hidden',
                    'px-[16px] gap-[8px] h-[35px] text-[14px] ml-auto md:ml-0  md:h-[35px] bg-[#E6E9FF] text-accent hover:bg-black hover:text-white'
                  )}>
                  <SearchIcon />{' '}
                  <span className='hidden sm:inline-block'>Поиск</span>
                </Button>
              </DrawerTrigger>
            </div>
            <nav
              className='hidden xl:block'
              itemScope
              itemType='https://schema.org/SiteNavigationElement'>
              <ul className='flex  gap-[6px] flex-wrap'>
                <DrawerTrigger asChild>
                  <Button
                    size='sm'
                    className='px-[16px] gap-[8px] h-[35px] text-[14px] ml-auto md:ml-0  md:h-[35px] bg-[#E6E9FF] text-accent hover:bg-black hover:text-white'>
                    <SearchIcon /> Поиск
                  </Button>
                </DrawerTrigger>
                {navLinks.map(el => (
                  <li
                    key={el.name}
                    className={cn(
                      'hover:underline relative  h-[35px] flex items-center px-[8px] rounded-[51px] text-[14px]',
                      isActive(el.slug)
                        ? 'bg-accent text-white'
                        : 'bg-[#F2F2F2]'
                    )}>
                    <Link
                      href={
                        [
                          '/bakalavriat',
                          '/magistratura',
                          '/kursy',
                          '/college'
                        ].includes(el?.slug ?? '')
                          ? `/programs${el?.slug}`
                          : el.slug
                      }
                      itemProp='url'
                      prefetch={false}>
                      <span itemProp='name'>{el.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className='ml-auto'>
              <Drawer
                direction='right'
                open={isBurgerOpen}
                onOpenChange={setIsBurgerOpen}>
                <DrawerTrigger asChild>
                  <Button
                    variant='primary'
                    className='px-[16px] w-[45px] h-[45px] text-[14px] md:hidden flex-col gap-[5px]'>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <span
                        className='block w-[13px] h-[1px] bg-white'
                        key={index}
                      />
                    ))}
                  </Button>
                </DrawerTrigger>
                <BurgerMenuDrawer categories={categories} />
              </Drawer>
              <div className='items-center gap-[16px] hidden md:flex'>
                <a
                  href='tel:+7(800)333-71-38'
                  className='text-right w-[130px]'>
                  <p className='text-[10px] text-[#8B8B8B] mb-2'>
                    Бесплатно по России
                  </p>
                  <p className='text-[14px] font-medium'>8 (800) 444-81-38</p>
                </a>
                <Button
                  size='sm'
                  onClick={() => setIsOpen(true)}
                  variant='primary'
                  className='px-[16px] flex items-center h-[45px] text-[14px]  md:h-[36px] md:text-[16px]'>
                  Оставить заявку
                </Button>
              </div>
            </div>
          </div>
          <nav
            className='xl:hidden mt-[14px] overflow-x-auto w-full no-scrollbar'
            itemScope
            itemType='https://schema.org/SiteNavigationElement'>
            <ul className='flex gap-[8px] lg:gap-[6px] md:min-w-[1067px] lg:min-w-[930px] min-w-[650px]'>
              <DrawerTrigger asChild>
                <Button
                  size='sm'
                  className={cn(
                    'px-[16px] gap-[8px] h-[35px] text-[14px] ml-auto  bg-[#E6E9FF] text-accent hover:bg-black hover:text-white hidden ',
                    'md:flex md:ml-0 md:h-[35px]'
                  )}>
                  <SearchIcon /> Поиск
                </Button>
              </DrawerTrigger>
              {navLinks.map(el => (
                <li
                  key={el.name}
                  className={cn(
                    'hover:underline relative  h-[35px] flex items-center px-[8px] rounded-[51px] text-[14px] text-nowrap',
                    isActive(el.slug) ? 'bg-accent text-white' : 'bg-[#F2F2F2]'
                  )}>
                  <Link
                    prefetch={false}
                    href={
                      [
                        '/bakalavriat',
                        '/magistratura',
                        '/kursy',
                        '/college'
                      ].includes(el?.slug ?? '')
                        ? `/programs${el?.slug}`
                        : el.slug
                    }
                    itemProp='url'>
                    <span itemProp='name'>{el.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <HeaderDrawer
          categories={categories}
          popularPrograms={popularPrograms}
        />
      </Drawer>

      <LeadFormDialog open={isOpen} onOpenChange={setIsOpen} />
    </header>
  )
}
